import { type ReactElement, useState } from 'react';
import cx from 'clsx';
import styles from './avatar.module.css';
import {
  AVATAR_SIZE_PX,
  type AvatarSize,
  getDisplayInitials,
  getIconSizeForAvatarSize,
} from './config';
import { AvatarBadgeOverlay } from './avatar-badge-overlay';
import type { AvatarProps } from './types';

const SIZE_CLASS = {
  'xx-small': styles.sizeXxSmall,
  'x-small': styles.sizeXSmall,
  small: styles.sizeSmall,
  medium: styles.sizeMedium,
  large: styles.sizeLarge,
  'x-large': styles.sizeXLarge,
} satisfies Record<AvatarSize, string | undefined>;

export { getInitials } from './content-renderer/helpers/get-initials';
export { getIconSizeForAvatarSize, type AvatarSize } from './config';
export type { AvatarBadge, AvatarEmptyProps, AvatarIconProps, AvatarProps } from './types';

// ── Avatar ────────────────────────────────────────────────────────────────────

export function Avatar(props: AvatarProps): ReactElement {
  const size = props.size ?? 'small';
  const { badge } = props;
  const badgeSize = getIconSizeForAvatarSize(size);

  // Image error state — always initialised so hooks remain unconditional.
  // Only read for type='image'.
  const [imageError, setImageError] = useState(false);

  const showingFallback =
    props.type === 'image' && (imageError || !props.src) && Boolean(props.fallbackInitials);

  // Both type='initials' and type='image' fallback render the same initials span
  // and share the same avatar-initials CSS class on the wrapper.
  const showingInitials = props.type === 'initials' || showingFallback;

  let content: ReactElement | null;

  if (props.type === 'image') {
    const {
      type: _t,
      size: _s,
      badge: _b,
      fallbackInitials,
      alt,
      'data-testid': _dt,
      ...imgProps
    } = props;

    if (showingFallback) {
      content = (
        <span data-testid="avatar-initials">
          {getDisplayInitials(fallbackInitials ?? '', size)}
        </span>
      );
    } else if (!imageError) {
      content = (
        <img
          alt={alt}
          {...imgProps}
          className={cx(styles.avatarImage, imgProps.className)}
          onError={() => setImageError(true)}
        />
      );
    } else {
      content = null;
    }
  } else if (props.type === 'initials') {
    content = (
      <span
        data-testid="avatar-initials"
        aria-hidden={props['aria-hidden']}
        aria-label={props['aria-label']}
      >
        {getDisplayInitials(props.initials, size)}
      </span>
    );
  } else if (props.type === 'icon') {
    const Icon = props.icon;
    const iconPx = AVATAR_SIZE_PX[size] / 2;
    content = (
      <Icon
        aria-hidden={props['aria-hidden'] ?? true}
        aria-label={props['aria-label']}
        height={iconPx}
        width={iconPx}
      />
    );
  } else {
    // type === 'empty'
    content = null;
  }

  const hasCustomBackground =
    (props.type === 'icon' || props.type === 'empty') && props.backgroundColor;
  const hasCustomIconColor = props.type === 'icon' && props.iconColor;

  // color is inherited, so setting it on the outer wrapper reaches the icon inside.
  // backgroundColor must live on the inner content wrapper so the CSS mask (also on
  // the inner wrapper) makes the ring gap transparent all the way to the page background.
  const outerStyle = hasCustomIconColor ? { color: props.iconColor } : undefined;
  const contentStyle = hasCustomBackground ? { backgroundColor: props.backgroundColor } : undefined;

  const showBorder = (props.withBorder ?? props.type === 'image') && !showingFallback;

  return (
    <div
      className={cx(styles.avatarWrapper, SIZE_CLASS[size])}
      data-test-avatar-wrapper
      data-testid={props['data-testid'] ?? 'avatar-wrapper'}
      style={outerStyle}
    >
      {/* Inner content wrapper carries the background and the CSS mask.
          Badge is a sibling (not a child) so Safari's border-box mask clip never touches it.
          avatarBorders and avatarDisabled live here so the mask cuts through both. */}
      <div
        className={cx(
          styles.avatarContentWrapper,
          showingInitials && styles.avatarInitials,
          (props.type === 'icon' || props.type === 'empty') && styles.avatarIconMain,
          showBorder && styles.avatarBorders,
          props.isDisabled && styles.avatarDisabled
        )}
        data-testid="avatar-content"
        style={contentStyle}
      >
        {content}
      </div>
      {badge && badgeSize ? <AvatarBadgeOverlay badge={badge} iconSize={badgeSize} /> : null}
    </div>
  );
}
